import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { ApiService } from 'src/app/shared/services/api.service';
import { LrsService, LrsRoute } from 'src/app/shared/services/lrs.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

@Component({
  selector: 'app-add-purchase-order',
  templateUrl: './add-purchase-order.component.html',
  styleUrl: './add-purchase-order.component.css',
})
export class AddPurchaseOrderComponent implements OnInit {
  purchaseorderForm!: FormGroup;
  selectedDomain = 'Residential';
  domainOptions: string[] = [
    'Residential', 'Commercial', 'Educational', 'Water', 'Power', 'Gas', 'Telecom', 'Road', 'Consultancy',
  ];
  optionsMap: { [key: string]: { value: string | number; label: string }[] } = {
    domainOptionsDrp: this.domainOptions.map((d) => ({ value: d, label: d })),
    vendorOrgOptions: [],
  };
  recordId: number | null = null;
  saving = false;
  lrsRoutes: LrsRoute[] = [];

  domainFields: { [key: string]: { key: string; label: string; type: string; value?: unknown }[] } = {
    Residential: [
      { key: 'tileTypeOrdered', label: 'Tile Type Ordered', type: 'text', value: '' },
      { key: 'paintBrand', label: 'Paint Brand', type: 'text', value: '' },
    ],
    Commercial: [
      { key: 'hvacUnits', label: 'HVAC Units', type: 'number', value: '' },
      { key: 'fireSafetyGear', label: 'Fire Safety Gear', type: 'text', value: '' },
    ],
    Educational: [
      { key: 'labFurniture', label: 'Lab Furniture', type: 'number', value: '' },
      { key: 'sportsEquipment', label: 'Sports Equipment', type: 'text', value: '' },
    ],
    Water: [
      { key: 'pipeSections', label: 'Pipe Sections', type: 'number', value: '' },
      { key: 'valveUnits', label: 'Valve Units', type: 'number', value: '' },
    ],
    Power: [
      { key: 'cableDrums', label: 'Cable Drums', type: 'number', value: '' },
      { key: 'transformerOil', label: 'Transformer Oil', type: 'text', value: '' },
    ],
    Gas: [
      { key: 'pressureRegulators', label: 'Pressure Regulators', type: 'number', value: '' },
      { key: 'safetyValves', label: 'Safety Valves', type: 'number', value: '' },
    ],
    Telecom: [
      { key: 'fiberCableSpools', label: 'Fiber Cable Spools', type: 'number', value: '' },
      { key: 'antennaUnits', label: 'Antenna Units', type: 'number', value: '' },
    ],
    Road: [
      { key: 'asphaltQuantity', label: 'Asphalt Quantity', type: 'text', value: '' },
      { key: 'roadSignageBoards', label: 'Road Signage Boards', type: 'number', value: '' },
    ],
    Consultancy: [
      { key: 'surveyEquipment', label: 'Survey Equipment', type: 'text', value: '' },
      { key: 'referenceMaterials', label: 'Reference Materials', type: 'text', value: '' },
    ],
  };

  commonFields = [
    { key: 'poId', label: 'PO ID', type: 'text', required: true, value: '' },
    { key: 'projectName', label: 'Project Name', type: 'text', required: true, value: '' },
    { key: 'projectId', label: 'Project ID', type: 'number', required: false, value: '' },
    { key: 'vendorOrgId', label: 'Vendor Organization', type: 'select', optionKey: 'vendorOrgOptions', required: true, value: '', onChange: (value: string) => this.onVendorOrgChange(value) },
    { key: 'vendorName', label: 'Vendor Name', type: 'text', required: true, value: '' },
    { key: 'poDate', label: 'PO Date', type: 'date', required: true, value: '' },
    { key: 'itemDescription', label: 'Item Description', type: 'text', required: true, value: '' },
    { key: 'quantity', label: 'Quantity', type: 'text', required: true, value: '' },
    { key: 'unitOfMeasure', label: 'Unit of Measure', type: 'text', required: true, value: '' },
    { key: 'unitPrice', label: 'Unit Price', type: 'text', required: true, value: '' },
    { key: 'totalValue', label: 'Total Value', type: 'text', required: true, value: '' },
    { key: 'deliveryLocation', label: 'Delivery Location', type: 'text', required: true, value: '' },
    { key: 'deliveryDate', label: 'Delivery Date', type: 'date', required: true, value: '' },
    { key: 'paymentTerms', label: 'Payment Terms', type: 'text', required: true, value: '' },
    { key: 'authorizedBy', label: 'Authorized By', type: 'text', required: true, value: '' },
    {
      key: 'domain',
      label: 'Domain',
      type: 'select',
      optionKey: 'domainOptionsDrp',
      required: true,
      value: 'Residential',
      onChange: (value: string) => this.onDomainChange(value),
    },
  ];

  pickerMap: { [key: string]: MatDatepicker<unknown> } = {};

  constructor(
    private fb: FormBuilder,
    private readonly apiService: ApiService,
    private readonly lrsService: LrsService,
    private readonly toastrMsgService: ToastrMsgService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.purchaseorderForm = this.fb.group({});
    this.commonFields.forEach((field) => {
      const validators = field.required ? [Validators.required] : [];
      this.purchaseorderForm.addControl(field.key, this.fb.control(field.value ?? '', validators));
    });
    this.onDomainChange(this.selectedDomain);
    this.purchaseorderForm.addControl('lrsRouteId', this.fb.control<number | null>(null));
    this.purchaseorderForm.addControl('lrsFromKm', this.fb.control<number | null>(null));
    this.purchaseorderForm.addControl('lrsToKm', this.fb.control<number | null>(null));
    this.purchaseorderForm.addControl('lrsChainage', this.fb.control(''));
    this.loadLrsRoutes();
    this.loadVendorOrgs();

    this.route.params.subscribe((params) => {
      const id = params['id'];
      if (id) {
        this.recordId = Number(id);
        this.loadRecord(this.recordId);
      }
    });
  }

  private loadRecord(id: number): void {
    this.apiService.getPurchaseOrderById(id).subscribe({
      next: (res: { data?: Record<string, unknown> }) => {
        const row = res.data || (res as Record<string, unknown>);
        const domainData = (row['domain_data'] as Record<string, unknown>) || {};
        this.purchaseorderForm.patchValue({
          poId: row['po_ref'],
          projectName: row['project_name'],
          projectId: row['project_id'],
          vendorOrgId: row['vendor_org_id'] || '',
          vendorName: row['vendor_name'],
          poDate: row['po_date'],
          itemDescription: row['item_description'],
          quantity: row['quantity'],
          unitOfMeasure: row['unit_of_measure'],
          unitPrice: row['unit_price'],
          totalValue: row['total_value'],
          deliveryLocation: row['delivery_location'],
          deliveryDate: row['delivery_date'],
          paymentTerms: row['payment_terms'],
          poStatus: row['po_status'],
          authorizedBy: row['authorized_by'],
          domain: row['domain'] || 'Residential',
        });
        this.onDomainChange(String(row['domain'] || 'Residential'));
        this.purchaseorderForm.patchValue(domainData);
        const lrs = (domainData['lrs'] as Record<string, unknown>) || {};
        this.purchaseorderForm.patchValue({
          lrsRouteId: lrs['route_id'] != null ? Number(lrs['route_id']) : null,
          lrsFromKm: lrs['from_km'] != null ? Number(lrs['from_km']) : null,
          lrsToKm: lrs['to_km'] != null ? Number(lrs['to_km']) : null,
          lrsChainage: lrs['chainage'] || '',
        });
        if (row['vendor_org_id'] && !this.optionsMap['vendorOrgOptions']?.some((opt) => String(opt.value) === String(row['vendor_org_id']))) {
          this.optionsMap['vendorOrgOptions'] = [
            {
              value: Number(row['vendor_org_id']),
              label: String(row['vendor_org_name'] || row['vendor_name'] || `Org #${row['vendor_org_id']}`),
            },
            ...(this.optionsMap['vendorOrgOptions'] || []),
          ];
        }
        this.loadLrsRoutes(row['project_id'] != null ? String(row['project_id']) : undefined);
      },
      error: (err) => this.toastrMsgService.error(err?.error?.message || 'Failed to load PO'),
    });
  }

  private buildPayload(): Record<string, unknown> {
    const v = this.purchaseorderForm.value;
    const domainKeys = (this.domainFields[this.selectedDomain] || []).map((f) => f.key);
    const domain_data: Record<string, unknown> = {};
    domainKeys.forEach((k) => {
      if (v[k] !== undefined && v[k] !== '') domain_data[k] = v[k];
    });
    if (v.lrsRouteId) {
      domain_data['lrs'] = {
        route_id: v.lrsRouteId,
        from_km: v.lrsFromKm,
        to_km: v.lrsToKm,
        chainage: v.lrsChainage || undefined,
      };
    }
    return {
      po_ref: v.poId,
      project_name: v.projectName,
      project_id: v.projectId || null,
      vendor_org_id: v.vendorOrgId || null,
      vendor_name: v.vendorName,
      po_date: v.poDate,
      item_description: v.itemDescription,
      quantity: v.quantity,
      unit_of_measure: v.unitOfMeasure,
      unit_price: v.unitPrice,
      total_value: v.totalValue,
      delivery_location: v.deliveryLocation,
      delivery_date: v.deliveryDate,
      payment_terms: v.paymentTerms,
      po_status: this.recordId ? undefined : 'draft',
      authorized_by: v.authorizedBy,
      domain: v.domain,
      domain_data,
    };
  }

  onSubmit(): void {
    if (!this.purchaseorderForm.valid) {
      this.purchaseorderForm.markAllAsTouched();
      return;
    }
    this.saving = true;
    const payload = this.buildPayload();
    const req = this.recordId
      ? this.apiService.updatePurchaseOrder(this.recordId, payload)
      : this.apiService.savePurchaseOrder(payload);

    req.subscribe({
      next: () => {
        this.saving = false;
        this.toastrMsgService.success(this.recordId ? 'Purchase order updated' : 'Purchase order saved');
        this.router.navigate(['/home/purchase-order-list']);
      },
      error: (err) => {
        this.saving = false;
        this.toastrMsgService.error(err?.error?.message || 'Save failed');
      },
    });
  }

  loadVendorOrgs(): void {
    this.apiService.listPurchaseOrderVendors().subscribe({
      next: (res: any) => {
        const rows = res?.data || [];
        this.optionsMap['vendorOrgOptions'] = (Array.isArray(rows) ? rows : []).map((org: any) => ({
          value: org.org_id,
          label: org.org_name || `Org #${org.org_id}`,
        }));
      },
      error: () => {
        this.optionsMap['vendorOrgOptions'] = [];
      },
    });
  }

  onVendorOrgChange(value: string | number): void {
    const selected = this.optionsMap['vendorOrgOptions']?.find((opt) => String(opt.value) === String(value));
    if (selected?.label) {
      this.purchaseorderForm.patchValue({ vendorName: selected.label });
    }
  }

  loadLrsRoutes(projectId?: string): void {
    this.lrsService.listRoutes(projectId ? { project_id: projectId } : undefined).subscribe({
      next: (res) => {
        this.lrsRoutes = res.data || [];
      },
      error: () => {
        this.lrsRoutes = [];
      },
    });
  }

  openPoOnMap(): void {
    const v = this.purchaseorderForm.value;
    const queryParams: Record<string, string | number> = { tab: 'lrs' };
    if (v.lrsRouteId) queryParams['route_id'] = v.lrsRouteId;
    if (v.lrsFromKm != null) queryParams['from_km'] = v.lrsFromKm;
    if (v.lrsToKm != null) queryParams['to_km'] = v.lrsToKm;
    if (v.projectId) queryParams['project_id'] = v.projectId;
    void this.router.navigate(['/home/geospatial'], { queryParams });
  }

  onReset(): void {
    this.purchaseorderForm.reset();
    this.onDomainChange(this.selectedDomain);
  }

  onDomainChange(domain: string): void {
    this.selectedDomain = domain;
    Object.keys(this.purchaseorderForm.controls).forEach((key) => {
      if (
        key !== 'domain' &&
        this.domainFields[domain]?.find((field) => field.key === key) === undefined &&
        Object.values(this.domainFields).flat().some((field) => field.key === key)
      ) {
        this.purchaseorderForm.removeControl(key);
      }
    });
    (this.domainFields[domain] || []).forEach((field) => {
      if (!this.purchaseorderForm.contains(field.key)) {
        this.purchaseorderForm.addControl(field.key, this.fb.control(field.value ?? ''));
      }
    });
  }
}
